import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg, rupee, pnlClass, STRATEGY_LABELS } from "@/lib/api";

const Stat = ({ k, v, id, cls = "text-slate-100" }) => (
  <div data-testid={id}><div className="text-[10px] uppercase tracking-wider text-slate-500">{k}</div><div className={`num text-sm font-semibold ${cls}`}>{v}</div></div>
);
const Mark = ({ ok }) => (ok ? <Check size={13} className="inline text-emerald-400" /> : <X size={13} className="inline text-red-400" />);

export const AdvisorTrack = ({ refresh }) => {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(true);
  const [busy, setBusy] = useState(false);
  const [day, setDay] = useState("");
  const load = useCallback(() => api.get("/advisor/track").then((r) => setD(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load, refresh]);
  const score = async () => {
    setBusy(true);
    try { const r = (await api.post("/advisor/score", null, { params: day ? { date: day } : {} })).data; toast.success(`Scored ${r.date}`); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!d) return null;
  const { stats, rows, regimes } = d;
  const pct = (v) => (v == null ? "—" : `${v}%`);
  return (
    <div className="mt-4 border-t border-slate-800 pt-3" data-testid="advisor-track">
      <div className="flex flex-wrap items-center gap-2">
        <button data-testid="button-track-toggle" onClick={() => setOpen((o) => !o)} className="eyebrow flex items-center gap-1 hover:text-slate-200">
          {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />} Track record · {stats.days ? `last ${stats.days} scored day${stats.days === 1 ? "" : "s"}` : "no scored days yet"}
        </button>
        <div className="ml-auto flex items-center gap-2">
          <select data-testid="select-track-day" value={day} onChange={(e) => setDay(e.target.value)}
            className="num h-7 rounded border border-slate-700 bg-slate-900 px-2 text-[11px] text-slate-200">
            <option value="">Latest finished day</option>
            {[...new Set([...(d.unscored || []), ...rows.map((r) => r.date)])].sort().reverse().map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
          <Button data-testid="button-track-score" size="sm" variant="outline" disabled={busy} onClick={score} className="h-7 border-slate-700 text-xs">{busy ? "Scoring…" : "Score now"}</Button>
        </div>
      </div>
      {open && (
        <>
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-5">
            <Stat k="Regime calls right" v={pct(stats.regime_hit_rate)} id="track-regime-rate" />
            <Stat k="Picks profitable & above median" v={pct(stats.pick_hit_rate)} id="track-pick-rate" />
            <Stat k="Avg best-pick rank" v={stats.avg_pick_rank == null ? "—" : `#${stats.avg_pick_rank} of ${rows[0]?.n_strategies || 9}`} id="track-avg-rank" />
            <Stat k="Picks' backtest P&L" v={rupee(stats.pick_bt_pnl)} cls={pnlClass(stats.pick_bt_pnl)} id="track-bt-pnl" />
            <Stat k="Picks' real P&L (paper+dry+live)" v={rupee(stats.real_pnl)} cls={pnlClass(stats.real_pnl)} id="track-real-pnl" />
          </div>
          {rows.length === 0 ? (
            <p className="mt-3 text-xs text-slate-500" data-testid="track-empty">No scored days yet. Each day is scored automatically at 16:00 IST, or tap Score now after the close.</p>
          ) : (
            <div className="mt-3 overflow-x-auto">
              <table className="dense num w-full" data-testid="table-advisor-track">
                <thead><tr><th className="!text-left">Date</th><th className="!text-left">Called</th><th className="!text-left">Actual (09:45→close)</th><th>Regime</th><th className="!text-left">Pick</th><th>Rank</th><th>Backtest</th><th>Real</th><th>Pick</th><th className="!text-left">Best that day</th></tr></thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.date} data-testid={`track-row-${r.date}`}>
                      <td className="!text-left text-slate-300">{r.date}{r.flipped_midday && <span className="ml-1 rounded bg-red-500/15 px-1 text-[9px] text-red-300">FLIP</span>}</td>
                      <td className="!text-left">{regimes[r.predicted] || r.predicted}</td>
                      <td className="!text-left text-slate-400">{r.actual ? `${regimes[r.actual.regime]} · ${r.actual.move_pct > 0 ? "+" : ""}${r.actual.move_pct}%` : "—"}</td>
                      <td><Mark ok={r.regime_hit} /></td>
                      <td className="!text-left">{r.picks.map((k) => STRATEGY_LABELS[k] || k).join(" + ")}</td>
                      <td>{r.best_pick_rank ? `#${r.best_pick_rank}` : "—"}</td>
                      <td className={pnlClass(r.pick_bt_pnl)}>{rupee(r.pick_bt_pnl)}</td>
                      <td className={pnlClass(r.real_pnl)}>{rupee(r.real_pnl)}</td>
                      <td><Mark ok={r.pick_hit} /></td>
                      <td className="!text-left text-slate-400">{r.best ? `${STRATEGY_LABELS[r.best] || r.best} ${rupee(r.all_bt[r.best])}` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};
